import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-6 -ml-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <h1
          className="text-3xl md:text-4xl font-medium text-stone-800 mb-2"
          style={{ fontFamily: 'Playfair Display, serif' }}
        >
          Privacy Policy
        </h1>
        <p className="text-stone-500 mb-8">Last Updated: March 2026</p>

        <div className="prose prose-stone max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">1. Who We Are</h2>
            <p className="text-stone-600 leading-relaxed">
              Mindful Renewal is a trauma-informed AI wellness platform operated by Visionary Vault Ventures LLC. 
              We are not a covered entity under HIPAA, however we treat all user data with the highest level of 
              care and discretion.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">2. What We Collect</h2>
            <ul className="text-stone-600 space-y-2">
              <li><strong>Account information:</strong> name, email address</li>
              <li><strong>Usage data:</strong> session frequency, feature usage</li>
              <li><strong>User-generated content:</strong> journal entries, chat logs, emotional check-ins</li>
              <li><strong>Device data:</strong> device type, operating system, IP address</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">3. What We Do Not Collect</h2>
            <ul className="text-stone-600 space-y-2">
              <li>We do not collect formal medical or clinical records</li>
              <li>We do not sell your data to third parties</li>
              <li>We do not use your journal entries for AI training</li>
              <li>We do not share your data with advertisers</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">4. How We Use Your Data</h2>
            <ul className="text-stone-600 space-y-2">
              <li>To personalize your Sage companion experience</li>
              <li>To improve app functionality and performance</li>
              <li>To send session reminders (with your permission)</li>
              <li>To provide customer support</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">5. Mental Health Data</h2>
            <p className="text-stone-600 leading-relaxed">
              We recognize that journal entries and emotional disclosures are deeply sensitive. This data is:
            </p>
            <ul className="text-stone-600 space-y-2 mt-2">
              <li>Encrypted at rest and in transit</li>
              <li>Accessible only by you</li>
              <li>Never reviewed by human staff without your consent</li>
              <li>Retained for 30 days after account deletion</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">6. Data Retention</h2>
            <ul className="text-stone-600 space-y-2">
              <li><strong>Active accounts:</strong> data retained while account is active</li>
              <li><strong>Deleted accounts:</strong> data purged within 30 days</li>
              <li>You may request immediate deletion at any time: privacy@mindfulrenewaltx.com</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">7. Crisis Disclosure</h2>
            <p className="text-stone-600 leading-relaxed">
              If you express immediate risk of harm to yourself or others, we may be required by law to 
              disclose information to appropriate authorities. This is the only circumstance under which 
              we would share your data without consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">8. Third Party Services</h2>
            <p className="text-stone-600 leading-relaxed mb-2">
              We use the following third-party services:
            </p>
            <ul className="text-stone-600 space-y-2">
              <li><strong>Stripe</strong> — payment processing</li>
              <li><strong>Emailit</strong> — email delivery</li>
            </ul>
            <p className="text-stone-600 mt-2">Each governed by their own privacy policies.</p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">9. Your Rights</h2>
            <p className="text-stone-600 leading-relaxed">You have the right to:</p>
            <ul className="text-stone-600 space-y-2 mt-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Delete your account and data</li>
              <li>Opt out of non-essential communications</li>
              <li>Data portability upon request</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">10. GDPR & International Users</h2>
            <p className="text-stone-600 leading-relaxed">
              If you are located in the EU or UK, you have additional rights under GDPR. 
              Contact us at privacy@mindfulrenewaltx.com to exercise these rights.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">11. Children's Privacy</h2>
            <p className="text-stone-600 leading-relaxed">
              Mindful Renewal is intended for users 18 and older. We do not knowingly collect data from minors.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-medium text-stone-800 mb-3">12. Contact</h2>
            <p className="text-stone-600 leading-relaxed">
              Visionary Vault Ventures LLC<br />
              privacy@mindfulrenewaltx.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};
